import { BaseRestRepository } from '~/repositories/baseRestRepository';
import { envConfig } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
import type {
  CompanyDamageWaiverDto,
  DamageWaiverPolicyDtoPaginationDto,
  EligibleLeasesResponseDto,
  GetDamageWaiverPoliciesParams,
  InsuranceStatisticsDto,
  LeaseDamageWaiverSettingsDto,
  OptLeaseInRequestDto,
  SendNoticeCampaignRequestDto,
  SendNoticeCampaignResponseDto,
  UpdateCompanyDamageWaiverSettingsRequestDto,
} from '~/swagger/tenantinsurance/Api';

const getTenantInsuranceBaseUrl = () => {
  const educationUrl = envConfig.service.education;
  return educationUrl.replace('/education', '/tenantinsurance');
};

const INSURANCE_SETTINGS_BASE_URL = `${getTenantInsuranceBaseUrl()}/company-portal`;

export class InsuranceRepository extends BaseRestRepository {
  public async getDamageWaiverPolicies(params?: GetDamageWaiverPoliciesParams): Promise<DamageWaiverPolicyDtoPaginationDto> {
    const url = urlWithQuery(`${INSURANCE_SETTINGS_BASE_URL}/insurance/damage-waivers/policies`, params);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getSettings(): Promise<CompanyDamageWaiverDto> {
    const url = `${INSURANCE_SETTINGS_BASE_URL}/insurance/damage-waivers/settings`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async updateSettings(payload: UpdateCompanyDamageWaiverSettingsRequestDto): Promise<void> {
    const url = `${INSURANCE_SETTINGS_BASE_URL}/insurance/damage-waivers/settings`;
    await this.fetchWithAuth(url, {
      method: 'patch',
      body: JSON.stringify(payload),
    });
  }

  //! SOME OF THESE ARE NOT SETTINGS, CAN MOVE OUT TO THE LEASE INSURANCE REPOSITORY
  public async getStatistics(): Promise<InsuranceStatisticsDto> {
    const url = `${INSURANCE_SETTINGS_BASE_URL}/insurance/statistics`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getLeaseDamageWaiverSettings(leaseId: string): Promise<LeaseDamageWaiverSettingsDto> {
    const url = `${INSURANCE_SETTINGS_BASE_URL}/insurance/damage-waivers/leases/${leaseId}/settings`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async optOutLease(leaseId: string): Promise<void> {
    const url = `${INSURANCE_SETTINGS_BASE_URL}/insurance/damage-waivers/leases/${leaseId}/opt-out`;
    await this.fetchWithAuth(url, { method: 'POST' });
  }

  public async optInLease(leaseId: string, payload: OptLeaseInRequestDto): Promise<void> {
    const url = `${INSURANCE_SETTINGS_BASE_URL}/insurance/damage-waivers/leases/${leaseId}/opt-in`;
    await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  public async getEligibleLeasesForNotice(): Promise<EligibleLeasesResponseDto> {
    const url = `${INSURANCE_SETTINGS_BASE_URL}/insurance/damage-waivers/notices/eligible-leases`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async sendNoticeCampaign(payload: SendNoticeCampaignRequestDto): Promise<SendNoticeCampaignResponseDto> {
    const url = `${INSURANCE_SETTINGS_BASE_URL}/insurance/damage-waivers/notices/campaigns`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }
}

export const insuranceRepo = new InsuranceRepository();
